import { useState, type ReactNode } from 'react';

import { Button } from '../../button';

import {
  actingUserLabels,
  clientLabels,
  decisionSummary,
  decisionTransition,
  dependencySummary,
  dependencyTransition,
  designLabels,
  failureCount,
  flowDone,
  flowFacts,
  flowSummary,
  flowTransition,
  hopLines,
  hopsFor,
  initialDecision,
  initialDependency,
  initialFlow,
  initialLog,
  logSummary,
  logTransition,
  OUTCOMES,
  outcomeSpecs,
  pendingCount,
  PROJECT_ID,
  replyFor,
  responseLines,
  tasksCreated,
  timeTaken,
  toActingUser,
  toClientKind,
  toDesign,
  toOutcome,
  totalCost,
  wiringLines,
  type ActingUser,
  type ClientKind,
  type DecisionAction,
  type DependencyAction,
  type Design,
  type FlowAction,
  type LogAction,
} from './feature-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './feature-labs.css';

function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const SIMULATION = "A simulation of CollabBoard's new route.";

const record = <K extends string>(labels: Record<K, string>, keys: readonly K[]) =>
  keys.map((key) => ({ value: key, label: labels[key] }));
const designOptions = record<Design>(designLabels, ['page-calls-model', 'page-calls-api']);
const clientOptions = record<ClientKind>(clientLabels, ['real', 'fixed', 'raising']);
const outcomeOptions = OUTCOMES.map((outcome) => ({
  value: outcome,
  label: outcomeSpecs[outcome].label,
}));
const actorOptions = record<ActingUser>(actingUserLabels, ['maya', 'sam']);

export function FeatureFlowLab() {
  const [state, setState] = useState(initialFlow);
  const act = (action: FlowAction) => setState((current) => flowTransition(current, action));
  const facts = flowFacts(state);
  const summary = flowSummary(state);
  const total = hopsFor(state.design).length;
  return (
    <Lab
      title="Where does the token live, and who can see it?"
      eyebrow="Feature model · where the token lives"
      className="rel-lab feature-lab"
    >
      <Predict>
        Who can see the token in this design? Press Next hop to follow it, then switch Design.
      </Predict>
      <div className="cb-controls">
        <label>
          Design
          <ModelSelect
            aria-label="Design"
            value={state.design}
            onValueChange={(value) => act({ type: 'design', value: toDesign(value) })}
            options={designOptions}
          />
        </label>
        <Button size="sm" disabled={flowDone(state)} onClick={() => act({ type: 'next' })}>
          Next hop
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label={`Hops · ${state.hop} of ${total}`}>
        <pre aria-label="Hops" className="feature-hops">
          {state.hop ? hopLines(state).join('\n') : '(no hop yet)'}
        </pre>
      </Panel>
      <Facts
        values={[
          ['Current hop', facts.currentHop],
          ['Token holder', facts.tokenHolder],
          ['Developer tools show', facts.devTools],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function FeatureDependencyLab() {
  const [state, setState] = useState(initialDependency);
  const act = (action: DependencyAction) =>
    setState((current) => dependencyTransition(current, action));
  const summary = dependencySummary(state);
  return (
    <Lab
      title="How does a test run the route without the model?"
      eyebrow="Feature model · the client as a dependency"
      className="rel-lab feature-lab"
    >
      <Predict>
        Does a fake client reach the model? Press Send POST /projects/{PROJECT_ID}/suggestions
        twice, then switch Client and send again.
      </Predict>
      <div className="cb-controls">
        <label>
          Client
          <ModelSelect
            aria-label="Client"
            value={state.client}
            onValueChange={(value) => act({ type: 'client', value: toClientKind(value) })}
            options={clientOptions}
          />
        </label>
        <Button size="sm" onClick={() => act({ type: 'send' })}>
          Send POST /projects/{PROJECT_ID}/suggestions
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="Wiring">
          <pre aria-label="Wiring">{wiringLines(state.client).join('\n')}</pre>
        </Panel>
        <Panel label="Response">
          <pre aria-label="Response">
            {state.sent === null
              ? '(nothing sent yet)'
              : responseLines(replyFor(state.sent)).join('\n')}
          </pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Model requests', String(state.modelRequests)],
          ...(state.sent === null ? [] : [['Time', timeTaken(state)] as [string, string]]),
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function FeatureLogLab() {
  const [state, setState] = useState(initialLog);
  const act = (action: LogAction) => setState((current) => logTransition(current, action));
  const summary = logSummary(state);
  return (
    <Lab
      title="What does one model call leave behind?"
      eyebrow="Feature model · the model_calls table"
      className="rel-lab feature-lab"
    >
      <Predict>
        Does a failed call leave a row? Press Ask for suggestions three times, then switch Outcome
        to model error and ask again.
      </Predict>
      <div className="cb-controls">
        <label>
          Outcome
          <ModelSelect
            aria-label="Outcome"
            value={state.outcome}
            onValueChange={(value) => act({ type: 'outcome', value: toOutcome(value) })}
            options={outcomeOptions}
          />
        </label>
        <Button size="sm" onClick={() => act({ type: 'ask' })}>
          Ask for suggestions
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="model_calls"
        className="feature-wide"
        columns={[
          'id',
          'prompt_version',
          'note_id',
          'prompt_tokens',
          'completion_tokens',
          'latency_ms',
          'outcome',
        ]}
        template="minmax(0, 0.5fr) minmax(0, 1.3fr) minmax(0, 0.8fr) minmax(0, 1.2fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1.2fr)"
        rows={state.rows.map((row) => [
          row.id,
          row.promptVersion,
          row.noteId,
          row.promptTokens,
          row.completionTokens,
          row.latencyMs,
          outcomeSpecs[row.outcome].stored,
        ])}
      />
      <Facts
        values={[
          ['Rows', String(state.rows.length)],
          ['Total cost', totalCost(state.rows)],
          ['Failures', String(failureCount(state.rows))],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function FeatureDecisionLab() {
  const [state, setState] = useState(initialDecision);
  const act = (action: DecisionAction) =>
    setState((current) => decisionTransition(current, action));
  const summary = decisionSummary(state);
  return (
    <Lab
      title="When does a suggestion become a task?"
      eyebrow="Feature model · proposals and decisions"
      className="rel-lab feature-lab"
    >
      <Predict>
        Can Sam turn a suggestion into a task? Press Accept as Maya, then switch Acting user to Sam
        and try again.
      </Predict>
      <div className="cb-controls">
        <label>
          Acting user
          <ModelSelect
            aria-label="Acting user"
            value={state.actor}
            onValueChange={(value) => act({ type: 'actor', value: toActingUser(value) })}
            options={actorOptions}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The page's suggestions panel">
        <ul className="feature-proposals" aria-label="Proposals">
          {state.suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <strong>{suggestion.title}</strong>
              <span className="feature-status" data-status={suggestion.status}>
                {suggestion.status}
              </span>
              <div className="cb-controls">
                <Button
                  size="sm"
                  disabled={suggestion.status !== 'pending'}
                  onClick={() => act({ type: 'accept', id: suggestion.id })}
                >
                  Accept {suggestion.title}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={suggestion.status !== 'pending'}
                  onClick={() => act({ type: 'dismiss', id: suggestion.id })}
                >
                  Dismiss {suggestion.title}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
      <div className="cb-two">
        <Table
          label="suggestions"
          className="feature-rows"
          columns={['id', 'title', 'status']}
          template="minmax(0, 0.5fr) minmax(0, 2fr) minmax(0, 1fr)"
          rows={state.suggestions.map((row) => [row.id, row.title, row.status])}
        />
        <Table
          label="tasks"
          className="feature-rows"
          columns={['id', 'title', 'project_id', 'assignee_id']}
          template="minmax(0, 0.5fr) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)"
          rows={state.tasks.map((row) => [
            row.id,
            row.title,
            row.projectId,
            row.assigneeId ?? 'NULL',
          ])}
        />
      </div>
      <Facts
        values={[
          ['Pending', String(pendingCount(state))],
          ['Tasks created', String(tasksCreated(state))],
          ...(state.lastResponse === null
            ? []
            : [['Last response', state.lastResponse] as [string, string]]),
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}
